/**
 * Aba "Fila pós-transferência" da Tela A2 (Config Tenant) — S4 Onda 4 / S7 PR2.
 *
 * Expõe o campo REAL `escalationLimboPolicy` do `tenant-config` (R15): o
 * comportamento da IA DEPOIS que uma conversa é transferida para a equipe humana
 * e aguarda alguém assumir (o "limbo da fila").
 *
 *   - `ackEnabled` (4a): um aviso honesto e determinístico ao cliente nos
 *     caminhos de transferência que hoje ficam MUDOS.
 *   - `receptionistEnabled` (4b): a IA SEGURA a fila em H_c até o claim, com
 *     postura pela classe do motivo (conduz útil / presença institucional /
 *     só registra). O rate-limit (teto/intervalo/atraso/demora) é aplicado em
 *     CÓDIGO — o modelo nunca decide quantas vezes insistir.
 *
 * Resolução efetiva única BE+FE: `resolveEscalationLimboPolicy` do @lume/shared —
 * a UI mostra exatamente o que o runtime fará. Escrita gated em `tenant-config-manage`;
 * a ABA só aparece sob a cap `ai-queue-receptionist` (gate na ConfigTenantPage — e, no
 * Admin Hub T9, via `omitWhenBarred` no catálogo: cap ausente ⇒ seção AUSENTE, corpo e
 * rail, não só escondida — reproduz o mesmo fail-closed com outro mecanismo).
 *
 * Admin Hub T9 fix round 1 — `<header>` próprio (h2 "Fila pós-transferência" + parágrafo)
 * removido: dentro de uma `DomainSection` o título/descrição já vêm do catálogo
 * (item.label/item.efeito), mesmo racional que `GeneralTab`(T7)/`ConductTab`/
 * `SilentTab`/`BehavioralTab`(T8)/`OperationProfileTab`(T9) já registraram. A frase
 * "sem isto, mensagens do cliente na fila ficam sem nenhum retorno" do parágrafo
 * removido foi dobrada em `hub/catalog.ts` (item `fila-pos-transferencia`) em vez de
 * virar um segundo heading — mesma disciplina que a T8 já aplicou.
 */

import { resolveEscalationLimboPolicy } from '@lume/shared';
import { useEffect, useMemo, useState } from 'react';

import { useTenantConfig, useUpdateTenantConfig } from './use-tenant-config';

import type { EscalationLimboPolicyRequest, SerializedTenantConfig } from './types';

interface FormState {
  ackEnabled: boolean;
  receptionistEnabled: boolean;
  maxQueueMessages: number;
  minIntervalSec: number;
  responseDelaySec: number;
  interveneAfterSec: number;
  /**
   * §D4 (26/Set/2026) — dono das tarefas que a IA cria para a equipe. Sem campo editável nesta
   * aba (a seção própria é fatia de UI futura); está no estado para VOLTAR no PATCH. Desde o
   * Item 522 o servidor mescla o envio sobre o gravado, então omitir já não apagaria — a volta
   * fica por ser inofensiva.
   */
  teamTaskOwnerUserId: string | null;
}

function toForm(c: SerializedTenantConfig): FormState {
  const p = resolveEscalationLimboPolicy(c.escalationLimboPolicy);
  return {
    ackEnabled: p.ackEnabled,
    receptionistEnabled: p.receptionistEnabled,
    maxQueueMessages: p.maxQueueMessages,
    minIntervalSec: p.minIntervalSec,
    responseDelaySec: p.responseDelaySec,
    interveneAfterSec: p.interveneAfterSec,
    teamTaskOwnerUserId: p.teamTaskOwnerUserId,
  };
}

/** Form → PATCH. Sempre envia o objeto completo; ambos toggles off = inerte (gate só liga com true). */
function buildPolicy(form: FormState): EscalationLimboPolicyRequest {
  return {
    ackEnabled: form.ackEnabled,
    receptionistEnabled: form.receptionistEnabled,
    maxQueueMessages: form.maxQueueMessages,
    minIntervalSec: form.minIntervalSec,
    responseDelaySec: form.responseDelaySec,
    interveneAfterSec: form.interveneAfterSec,
    teamTaskOwnerUserId: form.teamTaskOwnerUserId,
  };
}

const NUMERIC_FIELDS: readonly {
  key: 'maxQueueMessages' | 'minIntervalSec' | 'responseDelaySec' | 'interveneAfterSec';
  label: string;
  help: string;
  min: number;
  max: number;
}[] = [
  {
    key: 'maxQueueMessages',
    label: 'Máximo de mensagens da IA na fila',
    help: 'Teto absoluto de mensagens que a IA envia enquanto a conversa aguarda alguém assumir.',
    min: 0,
    max: 50,
  },
  {
    key: 'minIntervalSec',
    label: 'Intervalo mínimo entre mensagens (s)',
    help: 'Tempo mínimo entre duas mensagens da IA na fila (anti-spam).',
    min: 0,
    max: 3600,
  },
  {
    key: 'responseDelaySec',
    label: 'Atraso antes de responder (s)',
    help: 'Pausa deliberada antes de responder na fila — postura de recepção, não de condução.',
    min: 0,
    max: 120,
  },
  {
    key: 'interveneAfterSec',
    label: 'Demora antes da 1ª intervenção (s)',
    help: 'Só intervém depois deste tempo na fila (0 = responde de imediato).',
    min: 0,
    max: 86_400,
  },
];

export function QueueLimboTab({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();
  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (query.data !== undefined) setForm(toForm(query.data));
  }, [query.data]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data) : null),
    [query.data],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return JSON.stringify(form) !== JSON.stringify(serverForm);
  }, [form, serverForm]);

  if (query.isLoading) {
    return <p className="text-sm text-text-secondary">Carregando configurações…</p>;
  }
  if (query.isError || query.data === undefined || form === null) {
    return <p className="text-sm text-red-600">Falha ao carregar as configurações do tenant.</p>;
  }

  const fieldsDisabled = !canManage || update.isPending;

  const handleSave = (): void => {
    if (!isDirty) return;
    update.mutate({ escalationLimboPolicy: buildPolicy(form) });
  };

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  return (
    <div className="max-w-2xl">
      {/* Header próprio saiu (Admin Hub T9 fix round 1) — DomainSection já
          supre título (item.label) + descrição (item.efeito, dobrada com a
          frase que vivia aqui) quando esta aba é composta no hub. */}
      <div className="space-y-6">
        {/* 4a — aviso determinístico */}
        <div className="flex items-center justify-between rounded-xl border border-border-subtle bg-surface p-4">
          <div className="pr-4">
            <h3 className="text-sm font-medium text-text-primary">Aviso honesto ao cliente</h3>
            <p className="mt-0.5 text-xs text-text-secondary">
              Quando uma conversa é transferida, a IA envia um aviso claro (sem prometer prazo) de
              que a equipe vai assumir. Cobre inclusive os casos que hoje ficam mudos.
            </p>
          </div>
          <ToggleSwitch
            checked={form.ackEnabled}
            disabled={fieldsDisabled}
            label="Aviso honesto ao cliente"
            onToggle={() => setForm((f) => (f === null ? f : { ...f, ackEnabled: !f.ackEnabled }))}
          />
        </div>

        {/* 4b — modo recepcionista */}
        <div className="rounded-xl border border-border-subtle bg-surface p-4">
          <div className="flex items-center justify-between">
            <div className="pr-4">
              <h3 className="text-sm font-medium text-text-primary">
                Modo recepcionista (IA segura a fila)
              </h3>
              <p className="mt-0.5 text-xs text-text-secondary">
                A IA continua respondendo em postura de espera até um atendente assumir — a
                transferência segue de pé. A postura muda conforme o motivo da transferência
                (conduzir o que puder / presença institucional / só registrar em casos sensíveis).
              </p>
            </div>
            <ToggleSwitch
              checked={form.receptionistEnabled}
              disabled={fieldsDisabled}
              label="Modo recepcionista"
              onToggle={() =>
                setForm((f) =>
                  f === null ? f : { ...f, receptionistEnabled: !f.receptionistEnabled },
                )
              }
            />
          </div>

          {/* Rate-limit em código — só relevante com o recepcionista ligado */}
          <div className={`mt-4 space-y-4 ${form.receptionistEnabled ? '' : 'opacity-50'}`}>
            {NUMERIC_FIELDS.map(({ key, label, help, min, max }) => (
              <label key={key} className="block">
                <span className="block text-xs font-medium text-text-primary">{label}</span>
                <span className="mt-0.5 mb-1 block text-xs text-text-secondary">{help}</span>
                <input
                  type="number"
                  min={min}
                  max={max}
                  value={form[key]}
                  disabled={fieldsDisabled || !form.receptionistEnabled}
                  aria-label={label}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    const clamped = Math.min(max, Math.max(min, Number.isFinite(raw) ? raw : min));
                    setForm((f) => (f === null ? f : { ...f, [key]: clamped }));
                  }}
                  className="w-32 rounded border border-border-subtle bg-surface px-2 py-1.5 text-sm disabled:opacity-50"
                />
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      {canManage ? (
        <div className="mt-6 flex items-center justify-end gap-3">
          {update.isError && (
            <span role="alert" className="mr-auto text-sm text-red-600">
              Falha ao salvar. Tente novamente.
            </span>
          )}
          {update.isSuccess && !isDirty && (
            <span className="mr-auto text-sm text-emerald-600">Alterações salvas.</span>
          )}
          <button
            type="button"
            onClick={handleDiscard}
            disabled={!isDirty || update.isPending}
            className="rounded border border-border-subtle px-4 py-2 text-sm text-text-primary hover:bg-app-bg disabled:opacity-50"
          >
            Descartar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || update.isPending}
            className="rounded bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {update.isPending ? 'Salvando…' : 'Salvar alterações'}
          </button>
        </div>
      ) : (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A edição destas configurações é restrita ao administrador do
          tenant.
        </p>
      )}
    </div>
  );
}

function ToggleSwitch({
  checked,
  disabled,
  label,
  onToggle,
}: {
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly label: string;
  readonly onToggle: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
        checked ? 'bg-accent' : 'bg-border-subtle'
      }`}
    >
      <span
        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
